import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Pagination from '@mui/material/Pagination';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
    listAllEndorsements,
    listPendingSent,
    listPendingReceived,
    listPendingBadges,
    approveEndorsement,
    rejectEndorsement,
    approvePendingBadge,
    ApprovedEndorsement,
    PendingEndorsementRow,
    PendingBadgeRow,
} from '../api/endorsements';
import {
    listPendingTeamInvites,
    acceptTeamInvite,
    declineTeamInvite,
    listPendingIssuerShareRequests,
    approveIssuerShareRequest,
    declineIssuerShareRequest,
    listPendingBadgeShareRequests,
    approveBadgeShareRequest,
    declineBadgeShareRequest,
    PendingTeamInviteRow,
    PendingIssuerShareRow,
    PendingBadgeShareRow,
} from '../api/issuers';
import { useAuth } from '../hooks/useAuth';

type TabKey =
    | 'pendingBadges'
    | 'endorsements'
    | 'sent'
    | 'received'
    | 'teamInvites'
    | 'issuerShares'
    | 'badgeShares';

function formatDate(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function BadgeAvatar({ image, name }: { image: string | null; name: string }) {
    return (
        <Avatar
            src={image ?? undefined}
            alt={name}
            variant="rounded"
            sx={{ width: 48, height: 48, bgcolor: 'action.hover' }}
        >
            {!image && <WorkspacePremiumIcon />}
        </Avatar>
    );
}

function EmptyState({ message }: { message: string }) {
    return (
        <Box py={6} textAlign="center" color="text.secondary">
            <Typography variant="body2">{message}</Typography>
        </Box>
    );
}

function LoadingRows({ rows = 3 }: { rows?: number }) {
    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} />
            ))}
        </Box>
    );
}

// ── Pending Badges tab ─────────────────────────────────────────────────────
// Badges someone has issued to you, awaiting your acceptance.

function PendingBadgesTab({ onMutate }: { onMutate: () => void }) {
    const [rows, setRows] = useState<PendingBadgeRow[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(24);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPendingBadges(page);
        if (res.success) {
            setRows(res.data ?? []);
            setTotal(res.total);
            setPageSize(res.pageSize);
            setError(null);
        } else {
            setError(res.message ?? 'Failed to load pending badges.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { void load(); }, [load]);

    const handleAccept = async (id: string) => {
        const res = await approvePendingBadge(id);
        if (res.success) {
            setSnackbar('Badge accepted.');
            onMutate();
            void load();
        } else {
            setSnackbar(res.message ?? 'Failed to accept badge.');
        }
    };

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No badges waiting for your approval." />;

    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5}>
                {rows.map((row) => (
                    <Box
                        key={row.ID}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        p={2}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <BadgeAvatar image={row.Image} name={row.BadgeName} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography
                                component={RouterLink}
                                to={`/badges/${row.OpenBadgeID}`}
                                variant="body1"
                                fontWeight={500}
                                sx={{ textDecoration: 'none', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {row.BadgeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Issued {formatDate(row.CreatedDateTime)}
                                {row.ExpiresON ? ` · Expires ${formatDate(row.ExpiresON)}` : ''}
                            </Typography>
                        </Box>
                        <Button
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleAccept(row.ID)}
                        >
                            Accept
                        </Button>
                    </Box>
                ))}
            </Box>
            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
            )}
            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </>
    );
}

// ── Endorsements tab ───────────────────────────────────────────────────────
// Approved endorsements on badges you have issued (read-only).

function EndorsementsTab() {
    const [rows, setRows] = useState<ApprovedEndorsement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        listAllEndorsements().then((res) => {
            if (cancelled) return;
            if (res.success) {
                setRows(res.data ?? []);
                setError(null);
            } else {
                setError(res.message ?? 'Failed to load endorsements.');
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No approved endorsements yet." />;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {rows.map((e) => (
                <Box key={e.endorsementID} display="flex" gap={2} p={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <BadgeAvatar image={e.badgeImage} name={e.badgeName} />
                    <Box flexGrow={1} minWidth={0}>
                        <Typography
                            component={RouterLink}
                            to={`/badges/${e.badgeID}`}
                            variant="body2"
                            fontWeight={500}
                            sx={{ textDecoration: 'none', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {e.badgeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                            From {e.endorserName}
                            {e.endorserOrganization ? ` · ${e.endorserOrganization}` : ''} · {formatDate(e.dateCreated)}
                        </Typography>
                        <Typography variant="body2">{e.endorsementText}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// ── Sent Requests tab ──────────────────────────────────────────────────────
// Endorsement requests you've sent that haven't been responded to.

function SentRequestsTab() {
    const [rows, setRows] = useState<PendingEndorsementRow[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPendingSent(page);
        if (res.success) {
            setRows(res.data ?? []);
            setTotal(res.total);
            setPageSize(res.pageSize);
            setError(null);
        } else {
            setError(res.message ?? 'Failed to load sent requests.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { void load(); }, [load]);

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No outstanding endorsement requests." />;

    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5}>
                {rows.map((row) => (
                    <Box
                        key={row.EndorsementID}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        p={2}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <BadgeAvatar image={row.BadgeImage} name={row.BadgeName} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography
                                component={RouterLink}
                                to={`/badges/${row.BadgeID}`}
                                variant="body2"
                                fontWeight={500}
                                sx={{ textDecoration: 'none', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {row.BadgeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Sent to {row.EndorserName} · {formatDate(row.DateCreated)}
                            </Typography>
                            {row.EndorsementText && (
                                <Typography variant="body2" color="text.secondary" mt={0.5}>
                                    "{row.EndorsementText}"
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
            )}
        </>
    );
}

// ── Received Requests tab ──────────────────────────────────────────────────
// Endorsement requests received — others asking you to endorse their badge.

function ReceivedRequestsTab({ onMutate }: { onMutate: () => void }) {
    const { user } = useAuth();
    const defaultEndorserName = user?.displayName ?? '';
    const [rows, setRows] = useState<PendingEndorsementRow[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [approving, setApproving] = useState<PendingEndorsementRow | null>(null);
    const [endorserName, setEndorserName] = useState(defaultEndorserName);
    const [endorserOrganization, setEndorserOrganization] = useState('');
    const [endorsementText, setEndorsementText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPendingReceived(page);
        if (res.success) {
            setRows(res.data ?? []);
            setTotal(res.total);
            setPageSize(res.pageSize);
            setError(null);
        } else {
            setError(res.message ?? 'Failed to load received requests.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { void load(); }, [load]);

    const openApprove = (row: PendingEndorsementRow) => {
        setApproving(row);
        setEndorserName(defaultEndorserName);
        setEndorserOrganization('');
        setEndorsementText('');
    };

    const closeApprove = () => {
        if (submitting) return;
        setApproving(null);
    };

    const handleApprove = async () => {
        if (!approving) return;
        setSubmitting(true);
        const res = await approveEndorsement(approving.EndorsementID, {
            endorsementText: endorsementText.trim(),
            endorserName: endorserName.trim(),
            endorserOrganization: endorserOrganization.trim() || undefined,
        });
        setSubmitting(false);
        if (res.success) {
            setSnackbar('Endorsement sent.');
            setApproving(null);
            onMutate();
            void load();
        } else {
            setSnackbar(res.message ?? 'Failed to send endorsement.');
        }
    };

    const handleReject = async (id: string) => {
        const res = await rejectEndorsement(id);
        if (res.success) {
            setSnackbar('Request declined.');
            onMutate();
            void load();
        } else {
            setSnackbar(res.message ?? 'Failed to decline request.');
        }
    };

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No endorsement requests waiting on you." />;

    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const canSubmit = endorserName.trim().length > 0 && endorsementText.trim().length > 0 && !submitting;

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5}>
                {rows.map((row) => (
                    <Box
                        key={row.EndorsementID}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        p={2}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <BadgeAvatar image={row.BadgeImage} name={row.BadgeName} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography
                                component={RouterLink}
                                to={`/badges/${row.BadgeID}`}
                                variant="body2"
                                fontWeight={500}
                                sx={{ textDecoration: 'none', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {row.BadgeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Requested {formatDate(row.DateCreated)}
                            </Typography>
                            {row.EndorsementText && (
                                <Typography variant="body2" color="text.secondary" mt={0.5}>
                                    "{row.EndorsementText}"
                                </Typography>
                            )}
                        </Box>
                        <Box display="flex" gap={0.5}>
                            <Tooltip title="Decline">
                                <IconButton size="small" onClick={() => handleReject(row.EndorsementID)} aria-label="Decline">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Button
                                startIcon={<CheckCircleIcon />}
                                onClick={() => openApprove(row)}
                            >
                                Endorse
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>

            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
            )}

            <Dialog open={Boolean(approving)} onClose={closeApprove} maxWidth="sm" fullWidth>
                <DialogTitle>Endorse {approving?.BadgeName}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            label="Your name"
                            value={endorserName}
                            onChange={(e) => setEndorserName(e.target.value)}
                            required
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Organization (optional)"
                            value={endorserOrganization}
                            onChange={(e) => setEndorserOrganization(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Your endorsement"
                            value={endorsementText}
                            onChange={(e) => setEndorsementText(e.target.value)}
                            required
                            fullWidth
                            multiline
                            minRows={4}
                            placeholder="I endorse this badge because…"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={closeApprove} disabled={submitting}>Cancel</Button>
                    <Button onClick={handleApprove} disabled={!canSubmit}>
                        {submitting ? 'Sending…' : 'Send endorsement'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </>
    );
}

// ── Team Invites tab ───────────────────────────────────────────────────────
// Pending team-membership invitations directed at the current user.

function TeamInvitesTab({ onMutate }: { onMutate: () => void }) {
    const [rows, setRows] = useState<PendingTeamInviteRow[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState('');
    const [busyId, setBusyId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPendingTeamInvites(page);
        if (res.success) {
            setRows(res.data ?? []);
            setTotal(res.total);
            setPageSize(res.pageSize);
            setError(null);
        } else {
            setError(res.message ?? 'Failed to load team invites.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { void load(); }, [load]);

    async function handleAccept(issuerID: string) {
        setBusyId(issuerID);
        const res = await acceptTeamInvite(issuerID);
        setBusyId(null);
        if (res.success) {
            setSnackbar('Team invite accepted.');
            await load();
            onMutate();
        } else {
            setSnackbar(res.message ?? 'Failed to accept.');
        }
    }

    async function handleDecline(issuerID: string) {
        setBusyId(issuerID);
        const res = await declineTeamInvite(issuerID);
        setBusyId(null);
        if (res.success) {
            setSnackbar('Team invite declined.');
            await load();
            onMutate();
        } else {
            setSnackbar(res.message ?? 'Failed to decline.');
        }
    }

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No pending team invitations." />;

    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5}>
                {rows.map((row) => (
                    <Box
                        key={row.IssuerID}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        p={2}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <BadgeAvatar image={row.ImageFileName} name={row.IssuerName} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500}>
                                {row.IssuerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {row.IssuerType ?? 'Issuer'}
                                {row.HostDomain ? ` · ${row.HostDomain}` : ''}
                                {' · invited '}{formatDate(row.CreatedDate)}
                            </Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                            <Button
                                disabled={busyId === row.IssuerID}
                                onClick={() => handleAccept(row.IssuerID)}
                            >
                                Accept
                            </Button>
                            <Button
                                color="secondary"
                                disabled={busyId === row.IssuerID}
                                onClick={() => handleDecline(row.IssuerID)}
                            >
                                Decline
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>
            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
            )}
            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </>
    );
}

// ── Issuer Shares tab ──────────────────────────────────────────────────────
// Issuers asking to share their badges with one of yours.

function IssuerSharesTab({ onMutate }: { onMutate: () => void }) {
    const [rows, setRows] = useState<PendingIssuerShareRow[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState('');
    const [approveTarget, setApproveTarget] = useState<PendingIssuerShareRow | null>(null);
    const [acceptAllBadges, setAcceptAllBadges] = useState(false);
    const [declineTarget, setDeclineTarget] = useState<PendingIssuerShareRow | null>(null);
    const [declineMessage, setDeclineMessage] = useState('');
    const [blockFuture, setBlockFuture] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPendingIssuerShareRequests(page);
        if (res.success) {
            setRows(res.data ?? []);
            setTotal(res.total);
            setPageSize(res.pageSize);
            setError(null);
        } else {
            setError(res.message ?? 'Failed to load issuer share requests.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { void load(); }, [load]);

    async function submitApprove() {
        if (!approveTarget) return;
        setBusy(true);
        const res = await approveIssuerShareRequest(approveTarget.ID, { acceptAllBadges });
        setBusy(false);
        if (res.success) {
            setSnackbar('Issuer share approved.');
            setApproveTarget(null);
            setAcceptAllBadges(false);
            await load();
            onMutate();
        } else {
            setSnackbar(res.message ?? 'Failed to approve.');
        }
    }

    async function submitDecline() {
        if (!declineTarget) return;
        if (!declineMessage.trim()) {
            setSnackbar('A decline message is required.');
            return;
        }
        setBusy(true);
        const res = await declineIssuerShareRequest(declineTarget.ID, {
            message: declineMessage.trim(),
            blockFutureRequest: blockFuture,
        });
        setBusy(false);
        if (res.success) {
            setSnackbar('Issuer share declined.');
            setDeclineTarget(null);
            setDeclineMessage('');
            setBlockFuture(false);
            await load();
            onMutate();
        } else {
            setSnackbar(res.message ?? 'Failed to decline.');
        }
    }

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No pending issuer share requests." />;

    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5}>
                {rows.map((row) => (
                    <Box
                        key={row.ID}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        p={2}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <BadgeAvatar image={row.IssuerImageFileName_From} name={row.IssuerName_From} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500}>
                                {row.IssuerName_From}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                wants to share with {row.IssuerName_To} · {formatDate(row.LastUpdatedDate)}
                            </Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => { setApproveTarget(row); setAcceptAllBadges(false); }}
                            >
                                Approve
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => { setDeclineTarget(row); setDeclineMessage(''); setBlockFuture(false); }}
                            >
                                Decline
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>
            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
            )}

            <Dialog open={Boolean(approveTarget)} onClose={() => setApproveTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Approve issuer share</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" mb={2}>
                        Approving lets <strong>{approveTarget?.IssuerName_From}</strong> share badges with <strong>{approveTarget?.IssuerName_To}</strong>.
                    </Typography>
                    <Box component="label" display="flex" alignItems="center" gap={1}>
                        <input
                            type="checkbox"
                            checked={acceptAllBadges}
                            onChange={(e) => setAcceptAllBadges(e.target.checked)}
                        />
                        <Typography variant="body2">Automatically accept every badge they share</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveTarget(null)} disabled={busy}>Cancel</Button>
                    <Button onClick={submitApprove} variant="contained" disabled={busy}>Approve</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(declineTarget)} onClose={() => setDeclineTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Decline issuer share</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        label="Decline message"
                        value={declineMessage}
                        onChange={(e) => setDeclineMessage(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Box component="label" display="flex" alignItems="center" gap={1}>
                        <input
                            type="checkbox"
                            checked={blockFuture}
                            onChange={(e) => setBlockFuture(e.target.checked)}
                        />
                        <Typography variant="body2">Block future share requests from this issuer</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeclineTarget(null)} disabled={busy}>Cancel</Button>
                    <Button onClick={submitDecline} variant="contained" color="error" disabled={busy}>Decline</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </>
    );
}

// ── Badge Shares tab ───────────────────────────────────────────────────────
// Issuers offering to share a specific badge with one of yours.

function BadgeSharesTab({ onMutate }: { onMutate: () => void }) {
    const [rows, setRows] = useState<PendingBadgeShareRow[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState('');
    const [declineTarget, setDeclineTarget] = useState<PendingBadgeShareRow | null>(null);
    const [declineMessage, setDeclineMessage] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await listPendingBadgeShareRequests(page);
        if (res.success) {
            setRows(res.data ?? []);
            setTotal(res.total);
            setPageSize(res.pageSize);
            setError(null);
        } else {
            setError(res.message ?? 'Failed to load badge share requests.');
        }
        setLoading(false);
    }, [page]);

    useEffect(() => { void load(); }, [load]);

    async function handleApprove(id: string) {
        setBusy(true);
        const res = await approveBadgeShareRequest(id);
        setBusy(false);
        if (res.success) {
            setSnackbar('Badge share approved.');
            await load();
            onMutate();
        } else {
            setSnackbar(res.message ?? 'Failed to approve.');
        }
    }

    async function submitDecline() {
        if (!declineTarget) return;
        if (!declineMessage.trim()) {
            setSnackbar('A decline message is required.');
            return;
        }
        setBusy(true);
        const res = await declineBadgeShareRequest(declineTarget.ID, declineMessage.trim());
        setBusy(false);
        if (res.success) {
            setSnackbar('Badge share declined.');
            setDeclineTarget(null);
            setDeclineMessage('');
            await load();
            onMutate();
        } else {
            setSnackbar(res.message ?? 'Failed to decline.');
        }
    }

    if (loading) return <LoadingRows />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (rows.length === 0) return <EmptyState message="No pending badge share requests." />;

    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1.5}>
                {rows.map((row) => (
                    <Box
                        key={row.ID}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        p={2}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <BadgeAvatar image={row.Image} name={row.BadgeName} />
                        <Box flexGrow={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500}>
                                {row.BadgeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                from {row.IssuerName_From} to {row.IssuerName_To} · {formatDate(row.LastUpdatedDate)}
                            </Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                disabled={busy}
                                onClick={() => handleApprove(row.ID)}
                            >
                                Approve
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => { setDeclineTarget(row); setDeclineMessage(''); }}
                            >
                                Decline
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>
            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
            )}

            <Dialog open={Boolean(declineTarget)} onClose={() => setDeclineTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Decline badge share</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        label="Decline message"
                        value={declineMessage}
                        onChange={(e) => setDeclineMessage(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeclineTarget(null)} disabled={busy}>Cancel</Button>
                    <Button onClick={submitDecline} variant="contained" color="error" disabled={busy}>Decline</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </>
    );
}

// ── Page shell ─────────────────────────────────────────────────────────────

interface CountsState {
    pendingBadges: number;
    received: number;
    teamInvites: number;
    issuerShares: number;
    badgeShares: number;
}

const ZERO_COUNTS: CountsState = {
    pendingBadges: 0, received: 0, teamInvites: 0, issuerShares: 0, badgeShares: 0,
};

function CountBadge({ value, children }: { value: number; children: React.ReactNode }) {
    return (
        <Badge color="primary" badgeContent={value} sx={{ '& .MuiBadge-badge': { right: -16, top: 6 } }}>
            {children}
        </Badge>
    );
}

export default function NotificationsPage() {
    const [tab, setTab] = useState<TabKey>('pendingBadges');
    const [counts, setCounts] = useState<CountsState>(ZERO_COUNTS);

    const refreshCounts = useCallback(async () => {
        const [pb, rec, team, ishare, bshare] = await Promise.all([
            listPendingBadges(1),
            listPendingReceived(1),
            listPendingTeamInvites(1),
            listPendingIssuerShareRequests(1),
            listPendingBadgeShareRequests(1),
        ]);
        setCounts({
            pendingBadges: pb.success ? pb.total : 0,
            received: rec.success ? rec.total : 0,
            teamInvites: team.success ? team.total : 0,
            issuerShares: ishare.success ? ishare.total : 0,
            badgeShares: bshare.success ? bshare.total : 0,
        });
    }, []);

    useEffect(() => { void refreshCounts(); }, [refreshCounts]);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box mb={3}>
                <Typography variant="h4" fontWeight={700}>Notifications</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                    Pending badges, endorsement requests, team invitations, and issuer/badge sharing requests directed at you.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <Tabs
                    orientation="vertical"
                    value={tab}
                    onChange={(_, v) => setTab(v as TabKey)}
                    sx={{
                        minWidth: 220,
                        borderRight: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left', minHeight: 48, pr: 3 },
                    }}
                >
                    <Tab value="pendingBadges" label={<CountBadge value={counts.pendingBadges}>Pending Badges</CountBadge>} />
                    <Tab value="endorsements" label="Endorsements" />
                    <Tab value="sent" label="Sent Requests" />
                    <Tab value="received" label={<CountBadge value={counts.received}>Received Requests</CountBadge>} />
                    <Tab value="teamInvites" label={<CountBadge value={counts.teamInvites}>Team Invites</CountBadge>} />
                    <Tab value="issuerShares" label={<CountBadge value={counts.issuerShares}>Issuer Shares</CountBadge>} />
                    <Tab value="badgeShares" label={<CountBadge value={counts.badgeShares}>Badge Shares</CountBadge>} />
                </Tabs>

                <Box flex={1} minWidth={0}>
                    {tab === 'pendingBadges' && <PendingBadgesTab onMutate={refreshCounts} />}
                    {tab === 'endorsements' && <EndorsementsTab />}
                    {tab === 'sent' && <SentRequestsTab />}
                    {tab === 'received' && <ReceivedRequestsTab onMutate={refreshCounts} />}
                    {tab === 'teamInvites' && <TeamInvitesTab onMutate={refreshCounts} />}
                    {tab === 'issuerShares' && <IssuerSharesTab onMutate={refreshCounts} />}
                    {tab === 'badgeShares' && <BadgeSharesTab onMutate={refreshCounts} />}
                </Box>
            </Box>
        </Container>
    );
}
